<template>
  <div>
    <v-tabs right v-model="activeTab" class="mb-5" background-color="#233767" @change="getPerformanceData">
      <v-tab
        v-for="tab in tabs"
        :key="tab.value"
        :disabled="isLoading"
        style="min-width:50px"
      >
        {{ tab.text }}
      </v-tab>
    </v-tabs>

    <div v-resize="onResize" v-if="isLoading">
      <v-skeleton-loader type="card" :width="chartWidth"  :height="chartHeight" class="d-block mr-4 mb-3"></v-skeleton-loader>   
    </div> 
    <div v-resize="onResize"  v-if="!isLoading">
      <!-- Chart -->
      <svg class="caption" :width="chartWidth" :height="chartHeight" v-if="!error">
        <g :transform="`translate(${margin.left},${margin.top})`">
          <!-- X Axis -->
          <g :transform="`translate(0,${height})`">
            <g text-anchor="end">
              <g
                v-for="d in xTicks"
                :key="d"
                :transform="`translate(${x(d) + x.bandwidth() / 2},0)`"
              >
                <text fill="white" x="-9" dy="0.32em" transform="rotate(-90)">{{ d }}</text>
              </g>
            </g>
          </g>
          <!-- Y Axis -->
          <g>
            <g text-anchor="end">
              <g v-for="d in yTicks" :key="d" :transform="`translate(0,${y(d)})`">
                <text fill="white" x="-9" dy="0.32em">{{ formatPercentage(d) }}</text>
              </g>
            </g>
            <text
              fill="white"
              :transform="`translate(${-margin.left},${height / 2})rotate(-90)`"
              text-anchor="middle"
              dy="1em"
            >
              Daily Volume Variation
            </text>
          </g>
          <!-- Bars -->
          <g>
            <g
              v-for="d in performanceData"
              :key="d.date"
              :transform="`translate(${x(d[dateKey])},0)`"
              @mouseenter="onMouseEnter(d, $event)"
              @mousemove="onMouseMove($event)"
              @mouseleave="onMouseLeave"
            >
              <rect
                v-for="(e, i) in d.stacked"
                :key="i"
                :width="x.bandwidth()"
                :y="e.value > 0 ? y(e.stackedValues[1]) : y(e.stackedValues[0])"
                :height="Math.abs(y(e.stackedValues[0]) - y(e.stackedValues[1]))"
                :fill="color(e.key)"
              ></rect>
            </g>
          </g>
          <!-- Line -->
          <g style="{'poineter-event': 'none'}">
            <path
              fill="none"
              :stroke="lineColor"
              stroke-width="2"
              :d="linePath(performanceData)"
            ></path>
          </g>
        </g>
      </svg>
      <ChartLegend :items="legendItems" v-if="!error" />
      <ChartTooltip v-show="dActive" :position="pointerPosition">
        <v-simple-table dense>
          <template v-slot:default>
            <tbody>
              <tr>
                <td colspan="2">{{ dActive && dActive.date }}</td>
              </tr>
              <tr>
                <td>
                  <div class="d-flex align-center">
                    <div
                      :style="{
                        background: lineColor,
                        width: '16px',
                        height: '2px'
                      }"
                      class="mr-2"
                    ></div>
                    <div>{{ lineKey }}</div>
                  </div>
                </td>
                <td class="text-right">
                  {{ dActive && formatPercentage(dActive[lineKey]) }}
                </td>
              </tr>
              <tr v-for="(e, i) in dActive && dActive.stacked" :key="i">
                <td>
                  <div class="d-flex align-center">
                    <div
                      :style="{
                        background: color(e.key),
                        width: '16px',
                        height: '16px'
                      }"
                      class="mr-2"
                    ></div>
                    <div>{{ e.key }}</div>
                  </div>
                </td>
                <td class="text-right">{{ formatPercentage(e.value) }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </ChartTooltip>
      <div v-if="error">{{ error }}</div>
    </div>
  </div>
</template>

<script>
import { scaleBand, scaleLinear, scaleOrdinal } from "d3-scale";
import { descending, min, max } from "d3-array";
import { line } from "d3-shape";
import { format /*,precisionPrefix,formatPrefix*/ } from "d3-format";
import ChartLegend from "@/components/ChartLegend";
import ChartTooltip from "@/components/ChartTooltip";
export default {
  name: "VolumeBreakdownChart",
  components: {
    ChartLegend,
    ChartTooltip
  },
  props: {
    id: {
      type: String,
      required: true
    },
    apiEndpoint: {
      type: String,
      required: true
    },    
    dateKey: {
      type: String,
      default: "date"
    },
    barKeys: {
      type: Array,
      required: true
    },
    lineKey: {
      type: String,
      required: true
    },
    barColors: {
      type: Array,
      default: () => [
      '#0c2243',
      '#e9507f',  
      '#865986',
      '#bbc7da',
      '#3a99ec',
      '#38edd5',
      '#f8cbd9',
      '#f47a7a',
      '#ffff7f',
      '#FC6E51',
      '#A0CECB',
      ]
    },
    lineColor: {
      type: String,
      default: "#AB091E"
    },
    boolFormatPercent: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    activeTab: 0,
    tabs: [
      { text: '1m', value: '1', data: null},
      { text: '3m', value: '3', data: null},
      { text: '6m', value: '6', data: null},
      { text: '1y', value: '12', data: null},
    ],
    isLoading:true,
    performanceData: [],
    chartWidth: 960,
    chartHeight: 300,
    margin: {
      top: 8,
      right: 8,
      bottom: 80,
      left: 80
    },
    dActive: null,
    pointerPosition: [0, 0],
    error: ""
  }),
  computed: {
    width() {
      return this.chartWidth - this.margin.left - this.margin.right;
    },
    height() {
      return this.chartHeight - this.margin.top - this.margin.bottom;
    },
    xDomain() {
      return this.performanceData.map(d => d[this.dateKey]);
    },
    yDomain() {
      if (this.performanceData.length === 0) {
        return [0, 1];
      } else {
        const minValue = min(this.performanceData, d =>
          min(d.stacked, e => e.stackedValues[1])
        );
        const maxValue = max(this.performanceData, d =>
          max(d.stacked, e => e.stackedValues[1])
        );
        return [minValue, maxValue];
      }
    },
    x() {
      return scaleBand()
        .domain(this.xDomain)
        .range([0, this.width]);
    },
    y() {
      return scaleLinear()
        .domain(this.yDomain)
        .range([this.height, 0])
        .nice();
    },
    color() {
      return scaleOrdinal()
        .domain(this.barKeys)
        .range(this.barColors);
    },
    linePath() {
      return line()
        .x(d => this.x(d[this.dateKey]) + this.x.bandwidth() / 2)
        .y(d => this.y(d[this.lineKey]));
    },
    xTicks() {
      if (this.performanceData.length === 0) return [];
      const maxNumTicks = Math.round(this.width / 16);
      if (this.xDomain.length <= maxNumTicks) {
        return this.xDomain;
      } else {
        const n = Math.ceil(this.xDomain.length / maxNumTicks);
        return this.xDomain.filter((d, i) => i % n === 0);
      }
    },
    yTicks() {
      return this.performanceData.length === 0 ? [] : this.y.ticks(8);
    },
    legendItems() {
      return [
        ...this.barKeys.map(key => ({
          key,
          color: this.color(key),
          shape: "square"
        })),
        { key: this.lineKey, color: this.lineColor, shape: "line" }
      ];
    }
  },
  watch: {
    stock: {
      handler() {
        this.getPerformanceData();
      },
      immediate: true
    },
  },
  methods: {
    async getPerformanceData() {
        if (this.tabs[this.activeTab].data) {
          this.performanceData = [...this.tabs[this.activeTab].data]
          return
        }
        var self = this;
        this.isLoading=true;
        const currentSession = await this.$Amplify.Auth.currentSession()
        const currentToken = currentSession.getIdToken().getJwtToken()
        const url = `${process.env.VUE_APP_API_HOST}${this.apiEndpoint}${this.id}/${this.tabs[this.activeTab].value}`
        this.$http.get(url,{
          headers: {
            'Authorization': currentToken,
            'Content-Type': 'application/json'
          }
        }).then(res => {
          this.performanceData = res.data.volume.map(d => ({
            ...d,
            stacked: this.stackData(d)
          }));
          this.tabs[this.activeTab].data = [ ...this.performanceData ]
          this.isLoading=false;
        })
        .catch(function (error) {
          //console.log(error)
          self.activeTab = 0
          //this.performanceData = [...this.tabs[this.activeTab].data] || []     
          self.error = error.response.data.error;
          self.isLoading=false;
        });
    },
    stackData(d) {
      let totalPos = 0;
      let totalNeg = 0;
      const stackedPos = [];
      const stackedNeg = [];
      this.barKeys.forEach(key => {
        const value = d[key];
        if (value > 0) {
          stackedPos.push({
            key,
            value,
            stackedValues: [totalPos, totalPos + value]
          });
          totalPos += value;
        } else {
          stackedNeg.push({
            key,
            value,
            stackedValues: [totalNeg, totalNeg + value]
          });
          totalNeg += value;
        }
      });
      const stacked = [...stackedPos, ...stackedNeg];
      stacked.sort((a, b) => descending(Math.abs(a.value), Math.abs(b.value)));
      return stacked;
    },
    formatPercentage(d) {
      if (this.boolFormatPercent) {
        return format("0.0%")(d);
      } else {
        return format("~s")(d*1000);
        //var p = precisionPrefix(1e4, 1.3e6), f = formatPrefix("." + p, 1.3e6);
        //return f(d)
      }
    },
    onMouseEnter(d, e) {
      this.dActive = d;
      this.pointerPosition = [e.clientX, e.clientY];
    },
    onMouseMove(e) {
      this.pointerPosition = [e.clientX, e.clientY];
    },
    onMouseLeave() {
      this.dActive = null;
    },
    onResize() {
      this.chartWidth = this.$el.clientWidth;
    }
  }
};
</script>
